/**
 * Soft 2-note "salon chime" played via Web Audio API.
 * No audio file needed. Tone: warm, decaying bell — D5 + A5, sine waves.
 * Respects user's "chime muted" preference stored in localStorage.
 */

const CHIME_PREF_KEY = "lesalon:chime-muted";

let _audioCtx = null;
function getCtx() {
  if (typeof window === "undefined") return null;
  if (!_audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _audioCtx = new AC();
  }
  return _audioCtx;
}

export function isChimeMuted() {
  if (typeof window === "undefined") return false;
  return window.localStorage?.getItem(CHIME_PREF_KEY) === "1";
}

export function setChimeMuted(muted) {
  if (typeof window === "undefined") return;
  if (muted) window.localStorage.setItem(CHIME_PREF_KEY, "1");
  else window.localStorage.removeItem(CHIME_PREF_KEY);
}

/**
 * Play a soft two-note bell. Safe to call repeatedly.
 * Falls back silently if Web Audio API isn't available
 * or the user has interacted-zero with the page (browser autoplay policy).
 */
export function playChime() {
  if (isChimeMuted()) return;
  const ctx = getCtx();
  if (!ctx) return;
  // Resume in case context was suspended by autoplay policy.
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  const playTone = (freq, startOffset, duration = 0.9, peakGain = 0.08) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const start = now + startOffset;
    // Soft attack/decay envelope so it sounds like a tiny brass bell, not a beep.
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(peakGain, start + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    osc.start(start);
    osc.stop(start + duration + 0.05);
  };
  // D5 then A5 — a calm, peaceful interval (perfect fifth).
  playTone(587.33, 0);
  playTone(880.0, 0.14);
}
